import { useEffect } from 'react';
import { canonicalUrl } from './routes.js';

/**
 * Keeps the document's title, description and canonical link in step with the
 * page being shown.
 *
 * A single-page app hands every visitor the same `index.html`, so without this
 * every page would share one title and one description — the thing search
 * engines read first, and the thing they penalise when four pages claim to be
 * the same one. Search crawlers run the page's JavaScript before they read the
 * head, so writing these from React is enough; they do not have to be in the
 * served HTML.
 *
 * The canonical link is set alongside them because the app answers on more
 * than one host (the Render subdomain, the real domain) and an invite link
 * carries a `?code=` query. Naming the address a page should be indexed under
 * keeps those from being counted as separate pages with identical content.
 */
export interface DocumentMeta {
  title: string;
  description: string;
  /** A view name from `routes.ts`; anything else canonicalises to `/`. */
  view: string;
}

function setMetaContent(name: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLinkHref(rel: string, href: string): void {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Written straight to the DOM rather than through a head-management library:
 * three tags, one writer, and nothing to keep in sync.
 */
export function applyDocumentMeta({ title, description, view }: DocumentMeta): void {
  document.title = title;
  setMetaContent('description', description);
  // Open Graph and Twitter read their own tags, so a shared link that lands on
  // a content page shows that page rather than the app's front door.
  const og = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
  og?.setAttribute('content', title);
  const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
  ogDescription?.setAttribute('content', description);
  const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
  ogUrl?.setAttribute('content', canonicalUrl(view));
  setLinkHref('canonical', canonicalUrl(view));
}

/**
 * `null` means "not this component's business" — the app-level default and a
 * content page would otherwise both write, and the child's effect runs first,
 * so the default would win and every content page would share the app's title.
 */
export function useDocumentMeta(meta: DocumentMeta | null): void {
  const title = meta?.title;
  const description = meta?.description;
  const view = meta?.view;
  useEffect(() => {
    if (title === undefined || description === undefined || view === undefined) return;
    applyDocumentMeta({ title, description, view });
  }, [title, description, view]);
}
